import { SegmentSchema } from '@sondermind/segment';
import { PlatformType } from '@sondermind/utilities/services';
import { SelfReferralCtaLocationPage } from '@sondermind/utilities/models-client-facing';

/* eslint-disable @typescript-eslint/naming-convention */
export interface IClientFeedbackSubmittedData {
  feature: string;
  positive: boolean;
  metadata: {
    persona_provider_id: number;
  };
}

export interface IClientSelfReferralButtonData {
  /** Whether the CTA is for psychiatry */
  psychiatry: boolean;
  /** The page where the CTA was displayed */
  ctaLocationPage?: SelfReferralCtaLocationPage;
  /** Whether the client already has a provider of this service type */
  isExistingProviderType?: boolean;
  /** CTA variant */
  ctaVariant?: string;
}

export interface IDirectBookingStartedData {
  bookingState: string;
  personaClientId: number;
  accountComplete: boolean;
  requireAccountCompletionFlag: boolean;
}

export interface IReferralViewedMatchesData {
  referral_id: number;
  referral_name: string;
  referral_email: string;
  date_viewed: string;
}

export enum RequestToScheduleSegmentType {
  PAT = 'Propose a time',
  DIRECT_SCHEDULING = 'Direct Scheduling'
}

export interface IRequestToScheduleStarted {
  requestType: RequestToScheduleSegmentType;
  personaClientId: number;
  accountCompleted: boolean;
}
/* eslint-enable @typescript-eslint/naming-convention */

export interface IScheduleSessionStarted {
  rescheduling: boolean;
}

export const enum ProviderType {
  THERAPIST = 'therapist',
  PSYCHIATRIST = 'psychiatrist'
}

export interface IReplaceProviderStarted {
  providerPlatformIdentity: string;
  providerType: ProviderType;
}

export interface IDashboardLoadQuestionnaireAssigned {
  personaClientId: number;
  assignedQuestionnaireTypes: string[];
}

export interface IClientChatMessageSent {
  recipientPlatformAccountId: string;
  platform: PlatformType;
}

export interface IClientFileDownloaded {
  chatRoomId: string;
  clientPlatformIdentity: string;
  personaProviderId: number;
  fileName: string;
  fileType: string;
  fileSize: string;
  fileUuid: string;
}

export interface IClientStartsPreSessionCqs {
  personaClientId: number;
  assignedQuestionnaireTypes: string[];
  hoursUntilSession: number;
  sessionId: number;
}

export interface IClientHasCqsOnUpcomingSession {
  personaClientId: number;
  assignedQuestionnaireTypes: string[];
}

export interface IPostSessionSchedulingData {
  ctaClicked: boolean;
  abVariant: string;
}

export interface ITAQuestionnaireQuestionData {
  answer: string;
}

export interface IClientViewedDirectBookingCostEstimateData {
  coverageType: string;
  payerName: string;
  bookingState: string;
}

export enum ClientSegmentEvents {
  CLIENT_FEEDBACK_SUBMITTED = 'Client Feedback Submitted',
  CLIENT_PROVIDER_PROFILE_VIEWED = 'Client Provider Profile Viewed',
  CLIENT_VIEWED_DIRECT_BOOKING_COST_ESTIMATE = 'Client Viewed Direct Booking Cost Estimate',
  CLIENT_SELF_REFERRAL_BUTTON_VIEWED = 'Client Self Referral Button Viewed',
  CLIENT_SELF_REFERRAL_BUTTON_CLICKED = 'Client Self Referral Button Clicked',
  CLIENT_VIEWED_MATCHES = 'Client Viewed Matches',
  DIRECT_BOOKING_STARTED = 'Direct Booking Started',
  REFERRAL_VIEWED_PSYCH_MATCHES = 'Referral Viewed Psychiatry Matches',
  REQUEST_TO_SCHEDULE_STARTED = 'Request to Schedule Started',
  SCHEDULE_SESSION_STARTED = 'Schedule Session Started',
  REPLACE_PROVIDER_STARTED = 'Replace Provider Started',
  CLIENT_HAS_ACTIVE_CQS = 'Client Logged In, Has Assigned CQs',
  CLIENT_HAS_CQS_ON_UPCOMING_SESSION = 'Client Has CQs On Upcoming Session',
  CLIENT_STARTED_CQS_PRE_SESSION = 'Client started assigned CQs from session card',
  POST_SESSION_SCHEDULING = 'Post Session Scheduling',
  TA_QUESTIONNAIRE_QUESTION_1 = 'TA Questionnaire Question 1',
  TA_QUESTIONNAIRE_QUESTION_2 = 'TA Questionnaire Question 2',
  TA_QUESTIONNAIRE_QUESTION_3 = 'TA Questionnaire Question 3',

  // Chat
  CLIENT_CHAT_MESSAGE_SENT = 'Client Chat Message Sent',
  CLIENT_FILE_DOWNLOADED = 'Client File Attachment Downloaded',

  // ToS Modal
  TOS_MODAL_VIEWED = 'Client ToS Modal Viewed',
  TOS_MODAL_ACCEPTED = 'Client ToS Modal Accepted',

  // Goals
  GOALS_HOME_MODULE_VIEWED = 'Goals Home Module Viewed',
  VIEW_GOALS_DETAIL = 'View Goals Detail',
  GOAL_GENERATED = 'Goal Generated',
  ADD_GOAL = 'Add Goal',
  EDIT_GOAL_TEXT = 'Edit Goal Text',
  EDIT_GOAL_DETAILS = 'Edit Goal Details',
  ADD_GOAL_TEXT = 'Add Goal Text',
  DELETE_GOAL = 'Delete Goal',
  RE_OPEN_GOAL = 'Re-Open Goal',
  GOAL_SAVED = 'Goal Saved',
  GOALS_INTERSTITIAL_VIEWED = 'Goals Interstitial Viewed',
  GOALS_INTERSTITIAL_DISMISSED = 'Goals Interstitial Dismissed',
  GOALS_INTERSTITIAL_ADD_GOAL_TAPPED = 'Goals Interstitial Add Goal Tapped',

  // Post Session Mobile Banner
  POST_SESSION_MOBILE_BANNER_CLICKED = 'Post Session Mobile Banner Clicked',
  POST_SESSION_MOBILE_BANNER_DISMISSED = 'Post Session Mobile Banner Dismissed',
  POST_SESSION_MOBILE_BANNER_VIEWED = 'Post Session Mobile Banner Viewed',

  // Treatment Plan
  TREATMENT_PLAN_VIEWED = 'Treatment Plan Viewed by Client',
  CLIENT_TREATMENT_PLAN_SIGNED = 'Client Treatment Plan Signed',
  CLIENT_TREATMENT_PLAN_SHARED = 'Client Treatment Plan Shared',
  CLIENT_MESSAGES_PROVIDER_TREATMENT_PLAN = 'Client Messages Provider Treatment Plan',
}

export interface IClientTosModalData {
  isNewClientPortal: boolean;
}

export interface IClientProviderProfileViewedData {
  provider_platform_identity: string;
  provider_ai_bio_enabled: boolean;
}

export interface ITreatmentPlanClientEventData {
  providerId: number;
  planId: string;
}

export interface IClientViewedMatchesData {
  therapyTypes?: string[];
}

export interface IGoalsHomeModuleViewedData {
  page: string;
  component: string;
  platform: string;
  personaClientId: number;
}

export interface IViewGoalsDetailData {
  isMainGoal: boolean;
  source: string;
  page: string;
  platform: string;
  personaClientId: number;
}

export interface IGoalGeneratedData {
  goalSource: string;
  page: string;
  platform: string;
  personaClientId: number;
}

export interface IAddGoalData {
  location: string;
  platform: string;
  personaClientId: number;
}

export interface IEditGoalTextData {
  newGoal: boolean;
  source: string;
  platform: string;
  personaClientId: number;
}

export interface IEditGoalDetailsData {
  newGoal: boolean;
  source: string;
  platform: string;
  personaClientId: number;
}

export interface IDeleteGoalData {
  isArchived: boolean;
  source: string;
  platform: string;
  personaClientId: number;
}

export interface IReOpenGoalData {
  source: string;
  platform: string;
  personaClientId: number;
}

export interface IGoalSavedData {
  state: string;
  platform: string;
  personaClientId: number;
}

export interface IGoalsInterstitialViewedData {
  page: string;
  platform: string;
  personaClientId: number;
}

export interface IGoalsCommonContextData {
  platform: string;
  personaClientId: number;
}

export interface IPostSessionMobileBannerData {
  sessionNumber: number;
}

export interface IClientSegmentSchema extends SegmentSchema {
  [ClientSegmentEvents.CLIENT_FEEDBACK_SUBMITTED]: IClientFeedbackSubmittedData;
  [ClientSegmentEvents.CLIENT_PROVIDER_PROFILE_VIEWED]: IClientProviderProfileViewedData;
  [ClientSegmentEvents.CLIENT_VIEWED_DIRECT_BOOKING_COST_ESTIMATE]: IClientViewedDirectBookingCostEstimateData;
  [ClientSegmentEvents.CLIENT_SELF_REFERRAL_BUTTON_CLICKED]: IClientSelfReferralButtonData;
  [ClientSegmentEvents.CLIENT_SELF_REFERRAL_BUTTON_VIEWED]: IClientSelfReferralButtonData;
  [ClientSegmentEvents.CLIENT_VIEWED_MATCHES]: IClientViewedMatchesData;
  [ClientSegmentEvents.DIRECT_BOOKING_STARTED]: IDirectBookingStartedData;
  [ClientSegmentEvents.REFERRAL_VIEWED_PSYCH_MATCHES]: IReferralViewedMatchesData;
  [ClientSegmentEvents.REQUEST_TO_SCHEDULE_STARTED]: IRequestToScheduleStarted;
  [ClientSegmentEvents.SCHEDULE_SESSION_STARTED]: IScheduleSessionStarted;
  [ClientSegmentEvents.REPLACE_PROVIDER_STARTED]: IReplaceProviderStarted;
  [ClientSegmentEvents.CLIENT_HAS_ACTIVE_CQS]: IDashboardLoadQuestionnaireAssigned;
  [ClientSegmentEvents.CLIENT_HAS_CQS_ON_UPCOMING_SESSION]: IClientHasCqsOnUpcomingSession;
  [ClientSegmentEvents.CLIENT_CHAT_MESSAGE_SENT]: IClientChatMessageSent;
  [ClientSegmentEvents.CLIENT_STARTED_CQS_PRE_SESSION]: IClientStartsPreSessionCqs;
  [ClientSegmentEvents.POST_SESSION_SCHEDULING]: IPostSessionSchedulingData;
  [ClientSegmentEvents.TA_QUESTIONNAIRE_QUESTION_1]: ITAQuestionnaireQuestionData;
  [ClientSegmentEvents.TA_QUESTIONNAIRE_QUESTION_2]: ITAQuestionnaireQuestionData;
  [ClientSegmentEvents.TA_QUESTIONNAIRE_QUESTION_3]: ITAQuestionnaireQuestionData;
  [ClientSegmentEvents.CLIENT_FILE_DOWNLOADED]: IClientFileDownloaded;
  [ClientSegmentEvents.TOS_MODAL_VIEWED]: IClientTosModalData;
  [ClientSegmentEvents.TOS_MODAL_ACCEPTED]: IClientTosModalData;

  // Goals
  [ClientSegmentEvents.GOALS_HOME_MODULE_VIEWED]: IGoalsHomeModuleViewedData;
  [ClientSegmentEvents.VIEW_GOALS_DETAIL]: IViewGoalsDetailData;
  [ClientSegmentEvents.GOAL_GENERATED]: IGoalGeneratedData;
  [ClientSegmentEvents.ADD_GOAL]: IAddGoalData;
  [ClientSegmentEvents.EDIT_GOAL_TEXT]: IEditGoalTextData;
  [ClientSegmentEvents.EDIT_GOAL_DETAILS]: IEditGoalDetailsData;
  [ClientSegmentEvents.ADD_GOAL_TEXT]: IGoalsCommonContextData;
  [ClientSegmentEvents.DELETE_GOAL]: IDeleteGoalData;
  [ClientSegmentEvents.RE_OPEN_GOAL]: IReOpenGoalData;
  [ClientSegmentEvents.GOAL_SAVED]: IGoalSavedData;
  [ClientSegmentEvents.GOALS_INTERSTITIAL_VIEWED]: IGoalsInterstitialViewedData;
  [ClientSegmentEvents.GOALS_INTERSTITIAL_DISMISSED]: IGoalsCommonContextData;
  [ClientSegmentEvents.GOALS_INTERSTITIAL_ADD_GOAL_TAPPED]: IGoalsCommonContextData;
  [ClientSegmentEvents.POST_SESSION_MOBILE_BANNER_CLICKED]: IPostSessionMobileBannerData;
  [ClientSegmentEvents.POST_SESSION_MOBILE_BANNER_DISMISSED]: IPostSessionMobileBannerData;
  [ClientSegmentEvents.POST_SESSION_MOBILE_BANNER_VIEWED]: IPostSessionMobileBannerData;
  [ClientSegmentEvents.TREATMENT_PLAN_VIEWED]: ITreatmentPlanClientEventData;
  [ClientSegmentEvents.CLIENT_TREATMENT_PLAN_SIGNED]: ITreatmentPlanClientEventData;
  [ClientSegmentEvents.CLIENT_TREATMENT_PLAN_SHARED]: ITreatmentPlanClientEventData;
  [ClientSegmentEvents.CLIENT_MESSAGES_PROVIDER_TREATMENT_PLAN]: ITreatmentPlanClientEventData;
}
